import { Show, createSignal, onCleanup, onMount } from "solid-js";
import { getApiBaseUrl } from "../api/client";
import { t } from "../i18n";
import {
  isInsideTelegram,
  isTelegramMiniApp,
  tgAlert,
} from "../lib/telegram";
import { useAuth } from "../stores/auth";
import Section from "../components/tg/Section";

const BOT_USERNAME = (
  (import.meta.env.VITE_TELEGRAM_BOT_USERNAME as string | undefined) ?? ""
)
  .replace(/^@/, "")
  .trim();

export default function LoginPage() {
  const auth = useAuth();
  let container!: HTMLDivElement;
  const [widgetError, setWidgetError] = createSignal<string | null>(null);
  const [widgetStatus, setWidgetStatus] = createSignal<string | null>(null);
  const [retrying, setRetrying] = createSignal(false);

  const inTelegram = () => isInsideTelegram() || isTelegramMiniApp();
  const hasInitData = () => isTelegramMiniApp();

  async function retryMiniAppLogin() {
    setRetrying(true);
    try {
      await auth.loginWithWebApp();
    } catch (e) {
      console.error(e);
      await tgAlert(e instanceof Error ? e.message : t("AUTH_FAILED"));
      window.location.reload();
    } finally {
      setRetrying(false);
    }
  }

  onMount(() => {
    if (hasInitData() || !BOT_USERNAME) return;

    const handler = async (user: Record<string, string | number>) => {
      console.log("[auth] Telegram widget callback:", user);
      setWidgetError(null);
      setWidgetStatus("Signing in…");
      try {
        await auth.loginWithWidget(user);
        setWidgetStatus("OK — redirecting…");
        window.location.replace("/");
      } catch (e) {
        console.error("[auth] widget login failed:", e);
        const msg = e instanceof Error ? e.message : t("AUTH_FAILED");
        setWidgetStatus(null);
        setWidgetError(msg);
      }
    };

    (
      window as unknown as {
        onTelegramAuth?: (user: Record<string, string | number>) => void;
      }
    ).onTelegramAuth = handler;

    const script = document.createElement("script");
    script.src = "https://telegram.org/js/telegram-widget.js?22";
    script.async = true;
    script.setAttribute("data-telegram-login", BOT_USERNAME);
    script.setAttribute("data-size", "large");
    script.setAttribute("data-radius", "12");
    script.setAttribute("data-onauth", "onTelegramAuth(user)");
    script.setAttribute("data-request-access", "write");
    container.appendChild(script);
  });

  onCleanup(() => {
    delete (window as unknown as { onTelegramAuth?: unknown }).onTelegramAuth;
  });

  const d = () => ({
    ...auth.debug(),
    apiUrl: getApiBaseUrl(),
    botUsername: BOT_USERNAME || "(unset)",
  });

  return (
    <div class="login-stage relative flex h-dvh max-h-dvh flex-col overflow-hidden">
      <div class="relative z-[1] mx-auto flex min-h-0 w-full max-w-lg flex-1 flex-col overflow-y-auto px-4 pb-12 pt-10">
        <h1 class="page-title">{t("APP_NAME")}</h1>
        <p class="page-hero-line mb-4">{t("WELCOME_MESSAGE")}</p>

        <Show when={auth.error()}>
          <Section>
            <div class="px-4 py-3 text-[15px] text-[var(--destructive)]">
              {auth.error()}
            </div>
          </Section>
        </Show>

        <Show when={widgetError()}>
          <Section>
            <div class="px-4 py-3 text-[15px] text-[var(--destructive)]">
              {widgetError()}
            </div>
          </Section>
        </Show>

        <Show when={widgetStatus()}>
          <Section>
            <div class="px-4 py-3 text-[15px] text-[var(--link)]">
              {widgetStatus()}
            </div>
          </Section>
        </Show>

        <Show when={hasInitData()}>
          <Section footer={t("LOGIN_HINT")}>
            <div class="px-4 py-3 text-[15px] text-ink-muted">
              Telegram Mini App detected, but login to API failed.
            </div>
          </Section>
          <button
            type="button"
            class="btn-primary w-full"
            disabled={retrying()}
            onClick={retryMiniAppLogin}
          >
            {retrying() ? t("LOADING") : "Retry Mini App login"}
          </button>
        </Show>

        <Show when={!hasInitData()}>
          <Section footer={t("LOGIN_HINT")}>
            <Show when={inTelegram() && !hasInitData()}>
              <div class="px-4 py-3 text-[15px] text-ink-muted">
                Opened inside Telegram, but initData is empty. Use the bot Menu
                Button / Web App.
              </div>
            </Show>
            <div
              class="flex min-h-[50px] items-center justify-center px-4 py-3"
              ref={container}
            >
              {!BOT_USERNAME && (
                <p class="text-[15px] text-[var(--destructive)]">
                  Set VITE_TELEGRAM_BOT_USERNAME in frontend/.env
                </p>
              )}
            </div>
          </Section>
        </Show>

        <Show when={import.meta.env.DEV}>
          <details class="mt-4 text-left">
            <summary class="cursor-pointer text-[13px] text-ink-muted">
              Debug info
            </summary>
            <pre class="mt-2 overflow-x-auto rounded-[12px] bg-[var(--surface)] p-3 text-[11px] leading-relaxed text-ink-muted">
              {JSON.stringify(d(), null, 2)}
            </pre>
          </details>
        </Show>
      </div>
    </div>
  );
}
